import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Typography } from '@mui/material';
import { lazy, useCallback, useContext, useEffect, useMemo } from 'react';
import { useAction, useAppSelector, usePartialAppSelector, } from 'shared/hooks/redux/hooks';
import { pop_back_item, push_back_item } from 'console/utils/back-button';
import { isMediumUI } from 'shared/app/lib/choose-ui-type';
import { MessageBox } from 'shared/components/dialogs/message-box';
import { SettingsDialogWithRouter } from '../../dialogs/universal';
import { useBox, useCallbacks, usePartial } from 'shared/hooks/utils';
import { Z3kDialogFooter } from './layout/footer';
import { config_end_write, save_z3k_settings_ok_action, z3kSettingsSelectTab, Z3KSettingsUpdateReason, } from 'z3k-settings/redux/actions';
import { getZ3KDialogStateForDevice } from 'z3k-settings/redux/selectors';
import 'app/style/_small.sass';
import { Z3kConfigViewerDialog } from './layout/viewer';
import { getDeviceTime } from 'webui/utils/time';
import { selectDevice } from 'entities/device/lib';
import { TimedRenderer } from 'console/react-elements/timed-renderer';
import { Z3KDialogAppBarWithRouter } from './layout/app-bar-with-router';
import { BreadCrumbs } from 'console/dialogs/breadCrumbs';
import { setServiceModeManual } from 'console/redux/devices/items/actions';
import { useBooleanState } from 'shared/hooks/state/boolean';
import { useNavigateRoot } from 'shared/hooks/router';
import { set_device_service_mode } from 'console/redux/settings-actions';
import { ErrorBoundary } from 'shared/components/error-boundary';
import { Z3KSettings, Z3KSettingsEditor, } from 'z3k-settings/provider/types';
import { useMutationCallback } from 'shared/api/hooks';
import { isMobileApp } from 'utils';
import { useStartStoreReview } from 'entities/user.app-review/ui';
import CircularWithValueLabel from 'z3k-settings/components/material-utils/circular-progress-with-label';
export const LazyUi = lazy(async () => import(
// webpackChunkName: 'z3k-app'
'z3k-settings/layout'));
export function checkWiFiSettingsChanges(wifi_setting_in_ui, wifi_setting_in_device) {
    if (wifi_setting_in_ui == null) {
        return false;
    }
    return !(wifi_setting_in_device &&
        wifi_setting_in_device.netname === wifi_setting_in_ui?.netname &&
        wifi_setting_in_device.pass === wifi_setting_in_ui?.pass);
}
export function Z3KDialogContent() {
    const { config: z3k_config, getConfigAndCommands } = useContext(Z3KSettingsEditor);
    const need_app_review = useAppSelector((state) => state.need_app_review);
    const { device_id, device_spec, is_staff, service_view, timezone, device_name, get_device_settings, } = useContext(Z3KSettings);
    const device = usePartialAppSelector(selectDevice, device_id);
    const dialogState = useAppSelector(getZ3KDialogStateForDevice);
    const [invalid_dialog_open, closeInvalidDialog, openInvalidDialog] = useBooleanState(false);
    const [saveZ3kConfig, isLoadingSaveZ3kConfig, responseSaveZ3kConfig, errorSaveZ3kConfig, isSendSaveZ3kConfig,] = useMutationCallback('save_z3k_config');
    const [saveDeviceSettings, isLoadingSaveDeviceSettings, responseSaveDeviceSettings, errorSaveDeviceSettings,] = useMutationCallback('update_device');
    const loading = useMemo(() => isLoadingSaveDeviceSettings || isLoadingSaveZ3kConfig, [isLoadingSaveDeviceSettings, isLoadingSaveZ3kConfig]);
    const [opened_api_error, closeApiError, openApiError] = useBooleanState(false);
    useEffect(() => {
        if (errorSaveDeviceSettings != null || errorSaveZ3kConfig != null) {
            openApiError();
        }
    }, [errorSaveDeviceSettings, errorSaveZ3kConfig]);
    const [needClose, noNeedClose, setNeedClose] = useBooleanState(false);
    const saveZ3KSettingsOkAction = useAction(save_z3k_settings_ok_action);
    const doSave = useCallback((reason) => {
        if (reason == Z3KSettingsUpdateReason.Z3KDialogSave) {
            setNeedClose();
        }
        else {
            noNeedClose();
        }
        try {
            const [config, commands] = getConfigAndCommands();
            const change_wifi_settings = checkWiFiSettingsChanges(config.wifi_settings, device?.z3k_config?.wifi_settings);
            saveZ3KSettingsOkAction(device_id, config);
            // save(config, commands, device_id, device_settings, reason);
            saveZ3kConfig({
                device_id,
                config,
                commands,
                reason,
                change_wifi_settings,
            });
        }
        catch (error) {
            // we can catch validation errors
            openInvalidDialog();
            console.error(error);
        }
    }, [device_id, getConfigAndCommands]);
    const startReviewPlugin = useStartStoreReview();
    useEffect(() => {
        if (responseSaveZ3kConfig && isSendSaveZ3kConfig) {
            if (responseSaveZ3kConfig.ok) {
                if (isMobileApp() && need_app_review) {
                    startReviewPlugin()
                        .then(() => console.log('startReviewPlugin — done'))
                        .catch((err) => console.error('startReviewPlugin', JSON.stringify(err)));
                }
                const device_settings = get_device_settings();
                if (device_settings) {
                    saveDeviceSettings({ device_id, ...device_settings });
                }
                else {
                    if (needClose) {
                        close();
                    }
                }
            }
        }
    }, [responseSaveZ3kConfig]);
    const close = useNavigateRoot();
    useEffect(() => {
        if (needClose) {
            if (responseSaveDeviceSettings?.ok) {
                close();
            }
        }
    }, [responseSaveDeviceSettings]);
    const onApply = usePartial(doSave, Z3KSettingsUpdateReason.Z3KDialogApply);
    const onSave = usePartial(doSave, Z3KSettingsUpdateReason.Z3KDialogSave);
    const dispatchSelectTab = useAction(usePartial(z3kSettingsSelectTab, device_id ?? -1));
    const onSelectTab = useCallback((tab_key) => {
        if (dialogState.activeTab == null && tab_key != null) {
            // forward
            push_back_item(onBrowserBack);
        }
        if (dialogState.activeTab != null && tab_key == null) {
            // backward
            pop_back_item(onBrowserBack);
        }
        if (dialogState.activeTab != null && tab_key != null) {
            // teleport
        }
        dispatchSelectTab(tab_key);
    }, [dialogState.activeTab, dispatchSelectTab]);
    const onSelectTabBox = useBox(onSelectTab);
    const onBrowserBack = useCallback(() => {
        onSelectTabBox.current(null);
    }, []);
    const [reader_opened, closeReader] = useBooleanState(false);
    const password = useMemo(() => z3k_config?.usbpassword, [z3k_config?.usbpassword]);
    const is_medium_ui = useMemo(() => isMediumUI(), []);
    const manual = useAppSelector((state) => selectDevice(state, state.router_device_id)?.service_mode_manual);
    const setManual = useAction(setServiceModeManual);
    const setDeviceServiceMode = useAction(set_device_service_mode);
    const onSetServiceView = useCallback((isServiceView) => {
        if (device_id) {
            if (!manual) {
                setManual(device_id);
            }
            setDeviceServiceMode(device_id, isServiceView);
        }
    }, [device_id, manual, setManual]);
    const config_write_status = useMemo(() => device?.config_write_status ?? false, [device?.config_write_status]);
    const [open, setClose, setOpen] = useBooleanState(false);
    const closeMessageBox = useCallbacks([
        setClose,
        useAction(usePartial(config_end_write, device_id)),
    ]);
    const _onApply = useCallback(() => {
        onApply();
        if (device?.online) {
            setOpen();
        }
    }, [onApply, setOpen, device?.online]);
    return (_jsxs(ErrorBoundary, { children: [_jsxs(SettingsDialogWithRouter, { title: device_name, device_time: timezone != undefined && (_jsx(TimedRenderer, { active: true, interval: 10e3, children: () => (_jsxs(Typography, { variant: "h6", color: "primary", ml: 1, children: ["- ", getDeviceTime(timezone)] })) })), breadcrumbs: _jsx(BreadCrumbs, { config: z3k_config }), fullWidth: true, is_medium_ui: is_medium_ui, maxWidth: "md", content_sx: is_medium_ui ? { display: 'flex', flexFlow: 'row nowrap' } : undefined, contentWithZeroPadding: true, noIPhoneBottomPadding: true, fullScreenAppBar: is_medium_ui ? null : (_jsx(Z3KDialogAppBarWithRouter, { supportsServiceView: device_spec.has_service_mode, isServiceView: service_view, servicePassword: password, device_title: device_name, device_time: timezone != undefined && (_jsx(TimedRenderer, { active: true, interval: 10e3, children: () => (_jsxs(Typography, { variant: "h6", ml: 1, children: ["- ", getDeviceTime(timezone)] })) })), breadcrumbs: _jsx(BreadCrumbs, { mobileFlag: true, config: z3k_config }), showCloseButton: true, onSetServiceView: onSetServiceView, is_staff: is_staff })), footer: is_medium_ui ? (_jsx(Z3kDialogFooter, { supportsServiceView: device_spec.has_service_mode, is_service_view: service_view, password: password ?? 'admin', default_password: "admin", loading: loading, disabled: config_write_status, onSetServiceView: onSetServiceView, onSave: onSave, onApply: _onApply })) : null, iconState: device?.iconState, children: [_jsx(LazyUi, { loading: loading || config_write_status, onSave: onSave, onApply: _onApply }), _jsx(Z3kConfigViewerDialog, { open: reader_opened, close: closeReader })] }), _jsx(MessageBox, { title: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0441\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438", open: opened_api_error, isError: true, onClose: closeApiError, children: errorSaveZ3kConfig ?? errorSaveDeviceSettings }), _jsx(MessageBox, { title: "\u041F\u043E\u0434\u043E\u0436\u0434\u0438\u0442\u0435", open: loading, isWarning: true, children: "\u041A\u043E\u043D\u0444\u0438\u0433\u0443\u0440\u0430\u0446\u0438\u044F \u0437\u0430\u0433\u0440\u0443\u0436\u0430\u0435\u0442\u0441\u044F..." }), _jsx(MessageBox, { title: "\u0412 \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0430\u0445 \u0435\u0441\u0442\u044C \u043E\u0448\u0438\u0431\u043A\u0438", open: invalid_dialog_open, isError: true, onClose: closeInvalidDialog, children: "\u041D\u0443\u0436\u043D\u043E \u0438\u0441\u043F\u0440\u0430\u0432\u0438\u0442\u044C \u043F\u043E\u043B\u044F \u043E\u0442\u043C\u0435\u0447\u0435\u043D\u043D\u044B\u0435 \u043A\u0440\u0430\u0441\u043D\u044B\u043C \u0446\u0432\u0435\u0442\u043E\u043C" }), _jsxs(MessageBox, { open: open, onClose: closeMessageBox, children: [_jsx(Alert, { severity: "warning", sx: { mt: 1, display: 'flex', alignItems: 'center' }, children: "\u0412\u043D\u0438\u043C\u0430\u043D\u0438\u0435! \u041F\u0440\u043E\u0438\u0441\u0445\u043E\u0434\u0438\u0442 \u0437\u0430\u043F\u0438\u0441\u044C \u0432 \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u043E. \u041D\u0435 \u0432\u044B\u043A\u043B\u044E\u0447\u0430\u0439\u0442\u0435 \u043F\u0438\u0442\u0430\u043D\u0438\u0435 \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u0430, \u043D\u0435 \u043F\u0435\u0440\u0435\u0437\u0430\u0433\u0440\u0443\u0436\u0430\u0439\u0442\u0435 \u0435\u0433\u043E. \u042D\u0442\u043E \u043C\u043E\u0436\u0435\u0442 \u043F\u0440\u0438\u0432\u0435\u0441\u0442\u0438 \u043A \u043D\u0430\u0440\u0443\u0448\u0435\u043D\u0438\u044E \u0440\u0430\u0431\u043E\u0442\u043E\u0441\u043F\u043E\u0441\u043E\u0431\u043D\u043E\u0441\u0442\u0438. \u0414\u043E\u0436\u0434\u0438\u0442\u0435\u0441\u044C \u0437\u0430\u0432\u0435\u0440\u0448\u0435\u043D\u0438\u044F \u043E\u043F\u0435\u0440\u0430\u0446\u0438\u0438." }), _jsx(CircularWithValueLabel, { close: closeMessageBox, flagTheEnd: !config_write_status })] })] }));
}
